<script setup>
/**
 * One channel's page: every inbox of a single channel type.
 *
 * The Settings navigation lists every channel Tola supports, connected or not,
 * so each of those leaves needs somewhere to land. This page holds for none,
 * one, or ten inboxes: the connected ones sit in a table (name, status, how
 * many agents answer it, a Manage link into the inbox's own settings), an
 * unconnected channel gets a real page explaining what it is, and the header's
 * `New <channel>` hands over to the existing setup flow. Inbox configuration
 * itself is not rebuilt here - Manage links to `settings_inbox_show`.
 *
 * Email additionally carries an Addresses/Settings tab pair (`?tab=`): the
 * addresses are the inbox list, and Settings surfaces the workspace-wide reply
 * domain and reply email, which live on the account and are edited in General.
 * When neither of those features is enabled the tabs disappear and the page is
 * the single Addresses view.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  CHANNEL_CATALOGUE,
  availableChannels,
  inboxesForChannel,
} from 'dashboard/helper/channelCatalogue';
import { getInboxSource } from 'dashboard/helper/inbox';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsStatusChip from 'dashboard/components-next/Settings/SettingsStatusChip.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountScopedRoute, currentAccount } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');

const channel = computed(() =>
  CHANNEL_CATALOGUE.find(entry => entry.key === route.params.channel)
);

const features = computed(() => currentAccount.value?.features || {});

// A hand-typed or stale channel key has no page to show, and a channel this
// account's flags hide from the nav and All channels should not render here
// either. The features check waits for the account to load so a hard refresh
// on a valid deep link does not bounce.
watch(
  [channel, features],
  ([entry, loadedFeatures]) => {
    const featuresLoaded = Object.keys(loadedFeatures).length > 0;
    const unavailable =
      featuresLoaded &&
      entry &&
      !availableChannels(loadedFeatures).some(
        available => available.key === entry.key
      );
    if (!entry || unavailable) {
      router.replace(accountScopedRoute('settings_inbox_list'));
    }
  },
  { immediate: true }
);

watch(
  () => route.params.channel,
  () => store.dispatch('inboxes/get'),
  { immediate: true }
);

const connected = computed(() =>
  channel.value
    ? inboxesForChannel(inboxes.value, channel.value.channelType)
    : []
);

// The inbox payload carries no member count, so each listed inbox is asked
// once for its members. Counts arrive independently; a row shows an em dash
// until (or unless) its count does.
const agentCounts = ref({});
watch(
  connected,
  list => {
    list.forEach(async inbox => {
      if (inbox.id in agentCounts.value) return;
      agentCounts.value[inbox.id] = null;
      try {
        const response = await store.dispatch('inboxMembers/get', {
          inboxId: inbox.id,
        });
        agentCounts.value[inbox.id] = response.data.payload.length;
      } catch (error) {
        // The row keeps its em dash; the page is still useful without counts.
      }
    });
  },
  { immediate: true }
);

const setupRoute = computed(() =>
  accountScopedRoute('settings_inboxes_page_channel', {
    sub_page: route.params.channel,
  })
);

// Email's workspace-wide sending settings, mirroring the gates General uses
// to show the same fields.
const showReplyDomain = computed(
  () => !!features.value.inbound_emails && !!features.value.custom_reply_domain
);
const showReplyEmail = computed(
  () => !!features.value.inbound_emails && !!features.value.custom_reply_email
);
const hasEmailSettings = computed(
  () =>
    channel.value?.key === 'email' &&
    (showReplyDomain.value || showReplyEmail.value)
);

const tabs = computed(() => [
  { key: 'addresses', label: t('CHANNELS.EMAIL.TABS.ADDRESSES') },
  { key: 'settings', label: t('CHANNELS.EMAIL.TABS.SETTINGS') },
]);

const activeTab = computed(() => {
  if (!hasEmailSettings.value) return 'addresses';
  return route.query.tab === 'settings' ? 'settings' : 'addresses';
});

const onTabChange = tab => {
  if (tab.key === activeTab.value) return;
  const query = { ...route.query };
  delete query.tab;
  if (tab.key !== 'addresses') query.tab = tab.key;
  router.push({ name: route.name, params: route.params, query });
};

const sourceFor = inbox =>
  getInboxSource(inbox.channel_type, inbox.phone_number, inbox);

const openInbox = inbox =>
  router.push(accountScopedRoute('settings_inbox_show', { inboxId: inbox.id }));
</script>

<template>
  <SettingsPageShell
    v-if="channel"
    wide
    :title="$t(channel.labelKey)"
    :description="$t(channel.descriptionKey)"
  >
    <!-- With nothing connected the empty state below already carries the
         CTA; repeating it in the header is one too many. -->
    <template v-if="connected.length" #actions>
      <router-link :to="setupRoute">
        <Button
          size="sm"
          :label="
            $t('CHANNELS.CHANNEL_PAGE.NEW', { channel: $t(channel.labelKey) })
          "
        />
      </router-link>
    </template>

    <template v-if="hasEmailSettings" #tabs>
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTab === 'settings' ? 1 : 0"
        @tab-changed="onTabChange"
      />
    </template>

    <template v-if="activeTab === 'addresses'">
      <div
        v-if="connected.length"
        class="px-4 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <BaseTable
          :headers="[
            $t('CHANNELS.CHANNEL_PAGE.TABLE.NAME'),
            $t('CHANNELS.CHANNEL_PAGE.TABLE.STATUS'),
            $t('CHANNELS.CHANNEL_PAGE.TABLE.AGENTS'),
            '',
          ]"
          :items="connected"
        >
          <template #row="{ items }">
            <BaseTableRow v-for="inbox in items" :key="inbox.id" :item="inbox">
              <BaseTableCell>
                <div class="flex items-center gap-3 py-1">
                  <span
                    :class="channel.icon"
                    class="flex-shrink-0 size-5 text-n-slate-11"
                  />
                  <div class="flex flex-col min-w-0 gap-0.5">
                    <span class="block truncate text-body-main text-n-slate-12">
                      {{ inbox.name }}
                    </span>
                    <span
                      v-if="sourceFor(inbox)"
                      class="block truncate text-body-main text-n-slate-11"
                    >
                      {{ sourceFor(inbox) }}
                    </span>
                  </div>
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <SettingsStatusChip
                  v-if="inbox.disconnected_at"
                  :label="$t('CHANNELS.CARD.DISCONNECTED')"
                  tone="alert"
                />
                <SettingsStatusChip
                  v-else-if="inbox.reauthorization_required"
                  :label="$t('CHANNELS.CARD.REAUTHORIZATION_REQUIRED')"
                  tone="neutral"
                />
                <SettingsStatusChip
                  v-else
                  :label="$t('CHANNELS.CARD.CONNECTED')"
                  tone="success"
                />
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ agentCounts[inbox.id] ?? '—' }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <button
                  type="button"
                  class="text-sm underline text-n-slate-12 underline-offset-4 hover:text-n-slate-11"
                  @click="openInbox(inbox)"
                >
                  {{ $t('CHANNELS.CARD.MANAGE') }}
                </button>
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>

      <div v-else class="flex flex-col items-center gap-3 py-16 text-center">
        <h2 class="text-base font-medium text-n-slate-12">
          {{
            $t('CHANNELS.CHANNEL_PAGE.EMPTY_TITLE', {
              channel: $t(channel.labelKey),
            })
          }}
        </h2>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ $t(channel.descriptionKey) }}
        </p>
        <router-link :to="setupRoute">
          <Button size="sm" :label="$t('CHANNELS.CARD.CONNECT')" />
        </router-link>
      </div>
    </template>

    <SettingsSection
      v-else
      class="max-w-2xl"
      :title="$t('CHANNELS.EMAIL.SETTINGS_CARD.TITLE')"
      :description="$t('CHANNELS.EMAIL.SETTINGS_CARD.DESCRIPTION')"
    >
      <div
        v-if="showReplyDomain"
        class="flex items-center justify-between gap-4 px-4 py-3"
      >
        <span class="text-sm text-n-slate-12">
          {{ $t('CHANNELS.EMAIL.SETTINGS_CARD.DOMAIN_LABEL') }}
        </span>
        <span class="text-sm truncate text-n-slate-11">
          {{
            currentAccount?.domain || $t('CHANNELS.EMAIL.SETTINGS_CARD.NOT_SET')
          }}
        </span>
      </div>
      <div
        v-if="showReplyEmail"
        class="flex items-center justify-between gap-4 px-4 py-3"
      >
        <span class="text-sm text-n-slate-12">
          {{ $t('CHANNELS.EMAIL.SETTINGS_CARD.REPLY_EMAIL_LABEL') }}
        </span>
        <span class="text-sm truncate text-n-slate-11">
          {{
            currentAccount?.support_email ||
            $t('CHANNELS.EMAIL.SETTINGS_CARD.NOT_SET')
          }}
        </span>
      </div>
      <div class="flex px-4 py-3">
        <router-link :to="accountScopedRoute('general_settings_index')">
          <Button
            size="sm"
            ghost
            :label="$t('CHANNELS.EMAIL.SETTINGS_CARD.EDIT_IN_GENERAL')"
          />
        </router-link>
      </div>
    </SettingsSection>
  </SettingsPageShell>
</template>
